import { Header } from "@/components/Header";
import { StaticRouteBreadcrumbs } from "@/components/StaticRouteBreadcrumbs";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Helmet } from "react-helmet-async";
import { RotateCcw, CheckCircle, Truck } from "lucide-react";
import { RETURN_SHIPPING_COPY, RETURN_CONDITION_COPY } from "@/lib/shipping";

import { routeHead } from "@/lib/routeHead";

// Title/description come from scripts/routes.mjs, like the prerendered head.
const head = routeHead("/returns");


const ReturnsPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <link rel="canonical" href="https://loftloom.com/returns" />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:url" content="https://loftloom.com/returns" />
        <meta property="og:type" content="website" />
      </Helmet>
      <AnnouncementBar />
      <Header />
      <StaticRouteBreadcrumbs />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-16 md:py-24">
          <h1 className="page-title font-semibold mb-4 text-center">Returns</h1>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-12">You have 30 days from delivery to return an unused item in its original condition.</p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="text-center p-6 border border-border">
              <RotateCcw className="h-6 w-6 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold text-foreground mb-1">30-Day Returns</h3>
              <p className="text-sm text-muted-foreground">Return any item within 30 days of delivery for a refund.</p>
            </div>
            <div className="text-center p-6 border border-border">
              <Truck className="h-6 w-6 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold text-foreground mb-1">Return Shipping</h3>
              <p className="text-sm text-muted-foreground">{RETURN_SHIPPING_COPY}</p>
            </div>
            <div className="text-center p-6 border border-border">
              <CheckCircle className="h-6 w-6 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold text-foreground mb-1">How to start</h3>
              <p className="text-sm text-muted-foreground">Email us with your order number and we'll send return instructions.</p>
            </div>
          </div>

          <div className="space-y-8 text-sm text-muted-foreground leading-relaxed">
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-3">Return Policy</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>Items can be returned within 30 calendar days of delivery.</li>
                <li>{RETURN_CONDITION_COPY}</li>
                <li>{RETURN_SHIPPING_COPY}</li>
                <li>Once the return is received and inspected, the refund goes back to your original payment method. How long it takes to appear depends on your bank or card issuer.</li>
              </ul>
            </section>
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-3">How to initiate a return</h2>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Email hello@loftloom.com with your order number.</li>
                <li>We'll reply with a return authorisation and where to send the item.</li>
                <li>Once received and inspected, your refund will be issued to the original payment method.</li>
              </ol>
            </section>
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-3">Damaged or defective items</h2>
              <p>If your item arrives damaged, defective or incorrect, contact us as soon as you can — ideally within 48 hours of delivery — with photos. We cover return shipping in those cases and will arrange a replacement or refund.</p>
            </section>
            <div className="border-l-2 border-border bg-secondary/30 p-4 mt-6">
              <p className="text-foreground font-semibold text-[12px] uppercase tracking-wider mb-1">Exchanges</p>
              <p className="text-[13px]">Items are replaced only when they arrive damaged or defective. To change your mind about a piece, return it and place a new order.</p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReturnsPolicy;
